import { Injectable, signal, computed } from '@angular/core';

export enum LoadingStates {
  UNKNOWN = "UNKNOWN",
  REGISTER = "REGISTER",
  LOGIN = "LOGIN",
  REQUEST_PASSWORD = "REQUEST_PASSWORD",
  RESTORE_PASSWORD = "RESTORE_PASSWORD",
  PROJECTS_CREATE_EDIT = "PROJECTS_CREATE_EDIT",
  PROJECTS_DELETE = "PROJECTS_DELETE",
  PROJECTS_GET = "PROJECTS_GET",
  TAGS_CREATE_EDIT = "TAGS_CREATE_EDIT",
  TAGS_DELETE = "TAGS_DELETE",
  TAGS_GET = "TAGS_GET",
  THEMES_GET = "THEMES_GET",
  THEMES_GET_ITEM = "THEMES_GET_ITEM",
  CHATS_CREATE_EDIT = "CHATS_CREATE_EDIT",
  CHATS_DELETE = "CHATS_DELETE",
  CHATS_GET = "CHATS_GET",
  CHATS_GET_ITEM = "CHATS_GET_ITEM",
  CHATS_WS_CONNECT = "CHATS_WS_CONNECT",
  UPLOAD_CHAT_FILE = "UPLOAD_CHAT_FILE"
}

export interface LoadingItem {
  id: string;
  type: LoadingStates;
}

@Injectable({
  providedIn: "root",
})
export class LoadingService {
  readonly StatesNames = LoadingStates;

  // Используем Map для более эффективного доступа по ключу
  private loadingStatesMap = signal<Map<LoadingStates, boolean>>(
    new Map(Object.values(LoadingStates).map(state => [state, false]))
  );

  // Храним только активные загрузки
  private activeLoadingsSignal = signal<LoadingItem[]>([]);

  // Публичные API
  // @ts-ignore
  readonly stateIsActive = computed((state: LoadingStates) =>
    this.loadingStatesMap().get(state) || false
  );

  readonly isAnyLoading = computed(() => this.activeLoadingsSignal().length > 0);

  // Метод для проверки конкретного состояния
  isStateLoading(state: LoadingStates): boolean {
    return this.loadingStatesMap().get(state) || false;
  }

  // Метод для проверки нескольких состояний (любое из них активно)
  isAnyStateLoading(states: LoadingStates[]): boolean {
    return states.some(state => this.isStateLoading(state));
  }

  // Добавить загрузку и вернуть её ID
  startLoading(requestType = LoadingStates.UNKNOWN): string {
    const id = this.generateUUID();

    this.activeLoadingsSignal.update(loadings => [
      ...loadings,
      { id, type: requestType }
    ]);

    this.updateStateValue(requestType, true);

    return id;
  }

  // Завершить загрузку по ID
  finishLoading(id: string): void {
    const loadingItem = this.activeLoadingsSignal().find(item => item.id === id);

    if (!loadingItem) return;

    const type = loadingItem.type;

    // Удаляем загрузку
    this.activeLoadingsSignal.update(loadings =>
      loadings.filter(item => item.id !== id)
    );

    // Проверяем, остались ли другие загрузки этого типа
    const hasMoreOfSameType = this.activeLoadingsSignal().some(item => item.type === type);

    if (!hasMoreOfSameType) {
      this.updateStateValue(type, false);
    }
  }

  // Метод для прямого получения значения состояния
  getStateValue(state: LoadingStates): boolean {
    return this.loadingStatesMap().get(state) || false;
  }

  // Вспомогательные методы
  private updateStateValue(state: LoadingStates, value: boolean): void {
    this.loadingStatesMap.update(map => {
      const newMap = new Map(map);
      newMap.set(state, value);
      return newMap;
    });
  }

  private generateUUID(): string {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
